import { satisfiesRange } from '../model/semver.js';
import { blockComponent, formPages, layoutOf, navigationMode } from './form-layout.js';
const FOOTER_KEYWORDS = new Set(['previous', 'next', 'submit', 'progress', 'save_status', 'none']);
const NAVIGATION_LABEL = { single: 'Page unique', tabs: 'Onglets', wizard: 'Assistant', steps_with_summary: 'Étapes avec synthèse' };
const BINDING_ROOTS = new Set(['answers', 'inputs', 'state', 'aggregates', 'form', 'row', 'result', 'context']);
const PAGE_ROOTS = new Set(['answers', 'inputs']);
function fr(label, fallback) {
    return label?.['fr'] ?? (label === undefined ? undefined : Object.values(label)[0]) ?? fallback;
}
/** Toutes les chaînes d'une valeur, avec leur pointeur. */
function strings(value, pointer, out) {
    if (typeof value === 'string')
        out.push({ pointer, text: value });
    else if (Array.isArray(value))
        value.forEach((item, index) => strings(item, `${pointer}/${index}`, out));
    else if (value !== null && typeof value === 'object')
        for (const [key, item] of Object.entries(value))
            strings(item, `${pointer}/${key}`, out);
}
/** Racines des références d'une expression (`$answers.x` → `answers`), littéraux exclus. */
function roots(expression) {
    const found = new Set();
    const walk = (node) => {
        if (typeof node === 'string') {
            const match = /^\$([a-z][a-z0-9_]*)/.exec(node);
            if (match !== null)
                found.add(match[1]);
        }
        else if (Array.isArray(node))
            node.forEach(walk);
        else if (node !== null && typeof node === 'object') {
            for (const [key, value] of Object.entries(node))
                if (key !== '$literal')
                    walk(value);
        }
    };
    walk(expression);
    return [...found];
}
const DENSITY_RANK = { compact: 0, comfortable: 1, gloves: 2 };
/**
 * Recette effective d'un poste : celle de la surface ou de l'appareil, puis celle de l'étape. Chaque
 * contrainte se cumule (listes réunies, taille de cible la plus grande, densité la plus exigeante) : une
 * étape peut rendre la recette plus exigeante, jamais l'assouplir.
 */
export function mergeRecipes(recipes) {
    const merged = { recipe_ids: [], forbidden_block_categories: [], required_block_categories: [], forbidden_block_inputs: [], forbidden_navigation: [], forbidden_navigation_by_intent: {}, hidden_regions: [] };
    const union = (into, more) => {
        for (const value of more ?? [])
            if (!into.includes(value))
                into.push(value);
    };
    for (const { artifact_id, body } of recipes) {
        if (!merged.recipe_ids.includes(artifact_id))
            merged.recipe_ids.push(artifact_id);
        const c = body.constraints;
        if (c.min_target_px !== undefined)
            merged.min_target_px = Math.max(merged.min_target_px ?? 0, c.min_target_px);
        if (c.density !== undefined && (merged.density === undefined || DENSITY_RANK[c.density] > DENSITY_RANK[merged.density]))
            merged.density = c.density;
        union(merged.forbidden_block_categories, c.forbidden_block_categories);
        union(merged.required_block_categories, c.required_block_categories);
        union(merged.forbidden_block_inputs, c.forbidden_block_inputs);
        union(merged.forbidden_navigation, c.forbidden_navigation);
        // Le bandeau du formulaire ne se masque jamais, quelle que soit la recette.
        union(merged.hidden_regions, (c.hidden_regions ?? []).filter((role) => role !== 'header'));
        for (const [intent, modes] of Object.entries(c.forbidden_navigation_by_intent ?? {}))
            union((merged.forbidden_navigation_by_intent[intent] ??= []), modes);
    }
    return merged;
}
/** Recette par défaut d'une surface (`applies_to.default_for_surface`). */
export function defaultRecipeFor(recipes, surface) {
    return recipes.find((recipe) => recipe.body.applies_to.surfaces.includes(surface) && recipe.body.applies_to.default_for_surface === true);
}
/**
 * Recettes qui s'appliquent à un formulaire (ADR-146 §3.4) : celles par défaut des surfaces où son gabarit
 * de mise en page se rend (toutes les surfaces sans gabarit connu). Un seul calcul, pour la gate de
 * publication, la validation du brouillon et l'éditeur, qui applique la recette sans jamais l'afficher.
 */
export function applicableRecipes(recipes, layout) {
    const surfaces = layout?.surfaces ?? ['operator', 'web'];
    return recipes.filter((recipe) => recipe.body.applies_to.default_for_surface === true && recipe.body.applies_to.surfaces.some((surface) => surfaces.includes(surface)));
}
/** Recommandation de la recette pour une nature de formulaire (§3.4) : gabarit et mode pré-sélectionnés. */
export function recipeSuggestion(recipe, intent) {
    const found = (recipe.suggestions ?? []).find((suggestion) => suggestion.intent === intent);
    return found === undefined ? undefined : { layout_ref: found.layout_ref, navigation: found.navigation };
}
const GENERIC_RECIPE_MESSAGE = {
    RECIPE_NAVIGATION_FORBIDDEN: (mode) => `La navigation « ${mode} » ne convient pas à ce poste. Choisissez une autre navigation.`,
    RECIPE_NAVIGATION_FORBIDDEN_FOR_INTENT: (mode) => `La navigation « ${mode} » ne convient pas à ce formulaire. Choisissez une autre navigation.`,
    RECIPE_BLOCK_CATEGORY_FORBIDDEN: (block) => `Le bloc « ${block} » ne convient pas à ce poste. Retirez-le ou choisissez un autre bloc.`,
    RECIPE_BLOCK_INPUT_FORBIDDEN: (block) => `Le bloc « ${block} » demande une saisie qui ne convient pas à ce poste. Choisissez un autre bloc.`,
    RECIPE_BLOCK_CATEGORY_REQUIRED: (kind) => `Ce poste demande un bloc de type « ${kind} ». Ajoutez-en un.`,
};
const CATEGORY_LABEL = { field: 'champ', display: 'affichage', summary: 'synthèse', decision: 'décision', capture: 'saisie', navigation: 'navigation', section: 'section du formulaire' };
/** Actions déclarées d'un `ActionButton` (ADR-146 addendum (20)) : liste fermée, jamais de code ni d'appel d'API. */
export const FORM_BUTTON_ACTIONS = new Set(['next_page', 'previous_page', 'submit', 'add_row', 'open_page']);
/** Icônes d'un bloc : celles des options (`inspection-template-body`, `option_icon`), rendues au web et à la tablette. */
const BLOCK_ICONS = new Set(['check', 'close', 'alert', 'help', 'minus', 'plus', 'eye', 'eye-off', 'thermometer', 'droplet', 'gauge', 'clock', 'calendar', 'package', 'boxes', 'truck', 'scale', 'trash', 'refresh', 'shield', 'tag', 'user', 'home', 'image', 'lock', 'location', 'scan', 'paw', 'camera', 'arrow-right', 'circle']);
const OBJECT_ID = /^[0-9a-f]{24}$/;
const COMMON_KEY = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+){1,4}$/;
/**
 * Référence de médiathèque d'un bloc `FormImage` (`props.image`, littéral `media_ref`) : `file_id` (24 hex)
 * ou `common_key`, jamais les deux, jamais une URL. `undefined` si la valeur n'en est pas une.
 */
export function blockMediaRef(value) {
    const literal = value !== null && typeof value === 'object' && !Array.isArray(value) && '$literal' in value ? value.$literal : value;
    if (literal === null || typeof literal !== 'object' || Array.isArray(literal))
        return undefined;
    const ref = literal;
    if (Object.keys(ref).some((key) => key !== 'file_id' && key !== 'common_key' && key !== 'alt'))
        return undefined;
    if (typeof ref['file_id'] === 'string' && ref['common_key'] === undefined)
        return OBJECT_ID.test(ref['file_id']) ? { file_id: ref['file_id'] } : undefined;
    if (typeof ref['common_key'] === 'string' && ref['file_id'] === undefined)
        return COMMON_KEY.test(ref['common_key']) ? { common_key: ref['common_key'] } : undefined;
    return undefined;
}
/** Réglages propres aux blocs d'affichage et d'action (ADR-146 addendum (20)) : image, action, total. */
function checkDisplayBlock(name, block, pointer, blockName, scope) {
    const { error } = scope;
    const props = block.props ?? {};
    const icon = props['icon'];
    if ((name === 'ActionButton' || name === 'CounterChip') && icon !== undefined && (typeof icon !== 'string' || !BLOCK_ICONS.has(icon))) {
        error(`${pointer}/props/icon`, 'LAYOUT_BLOCK_ICON_UNKNOWN', `L’icône du bloc « ${blockName} » n’est pas dans la liste. Choisissez une icône de la liste.`, { icon });
    }
    if (name === 'FormImage') {
        if (blockMediaRef(props['image']) === undefined)
            error(`${pointer}/props/image`, 'LAYOUT_BLOCK_IMAGE_INVALID', `Le bloc « ${blockName} » n’a pas d’image de la médiathèque. Choisissez une image dans la médiathèque (une adresse internet n’est pas admise).`);
        return;
    }
    if (name === 'ActionButton') {
        const action = props['action'];
        if (typeof action !== 'string' || !FORM_BUTTON_ACTIONS.has(action)) {
            error(`${pointer}/props/action`, 'LAYOUT_BLOCK_ACTION_UNKNOWN', `Le bouton « ${blockName} » ne déclenche aucune action connue. Choisissez son action dans la liste.`, { action });
            return;
        }
        if (action === 'add_row') {
            const section = typeof props['section'] === 'string' ? scope.sections.get(props['section']) : undefined;
            if (section === undefined || section.series === undefined)
                error(`${pointer}/props/section`, 'LAYOUT_BLOCK_ACTION_SECTION_UNKNOWN', `Le bouton « ${blockName} » ajoute une ligne à une liste qui n’existe pas. Choisissez une liste de lignes du formulaire.`, { section: props['section'] });
        }
        if (action === 'open_page' && (typeof props['page'] !== 'string' || !scope.pageIds.has(props['page']))) {
            error(`${pointer}/props/page`, 'LAYOUT_BLOCK_ACTION_PAGE_UNKNOWN', `Le bouton « ${blockName} » ouvre une page qui n’existe pas. Choisissez une page du formulaire.`, { page: props['page'] });
        }
        return;
    }
    if (name === 'CounterChip') {
        const path = block.bind?.['value']?.path;
        const match = path === undefined ? null : /^\$aggregates\.([a-z][a-z0-9_]*)$/.exec(path);
        if (match === null || !scope.aggregateIds.has(match[1]))
            error(`${pointer}/bind/value`, 'LAYOUT_BLOCK_AGGREGATE_UNKNOWN', `La puce « ${blockName} » n’affiche aucun total déclaré. Choisissez un total de la fiche.`, { path });
    }
}
/**
 * Vérifie la couche de mise en page d'un gabarit. Un gabarit sans `layout` n'a rien à vérifier : il se
 * rend comme avant. Les avertissements (`severity: warning`) ne refusent rien.
 */
export function checkFormLayout(template, context) {
    const layout = layoutOf(template);
    if (layout === undefined)
        return [];
    const findings = [];
    const error = (path, code, message, details) => {
        findings.push({ path, code, message, severity: 'error', ...(details === undefined ? {} : { details }) });
    };
    const warn = (path, code, message, details) => {
        findings.push({ path, code, message, severity: 'warning', ...(details === undefined ? {} : { details }) });
    };
    const sections = new Map(template.sections.map((section) => [section.section_id, section]));
    const sectionName = (id) => fr(sections.get(id)?.label, 'sans nom');
    // Gabarits de texte : le défaut du v1 (ADR-144). Aucune chaîne `{{…}}` dans la couche de mise en page.
    const texts = [];
    strings(layout, '/layout', texts);
    for (const entry of texts)
        if (entry.text.includes('{{'))
            error(entry.pointer, 'LAYOUT_TEMPLATE_STRING_FORBIDDEN', 'Un texte contient des accolades doubles : écrivez le texte tel qu’il doit s’afficher, ou choisissez une valeur du formulaire dans la liste.', { text: entry.text });
    if (template.steps !== undefined && layout.pages !== undefined) {
        error('/layout/pages', 'LAYOUT_STEPS_AND_PAGES', 'Ce formulaire déclare ses étapes deux fois. Gardez seulement les pages de la mise en page.');
    }
    const body = context.layout?.body;
    if (context.layout === null) {
        error('/layout/layout_ref', 'LAYOUT_REF_UNKNOWN', 'La mise en page choisie n’existe pas ou n’est plus disponible. Choisissez une autre mise en page.', { layout_ref: layout.layout_ref });
    }
    const regions = new Map((body?.regions ?? []).map((region) => [region.region_id, region]));
    const regionName = (id) => fr(regions.get(id)?.label, 'sans nom');
    const layoutName = fr(body?.label, 'choisie');
    // Navigation : permise par le gabarit, puis par la recette (en paysage et dans chaque format déclaré).
    const formats = ['landscape', 'portrait', 'mobile', 'web'];
    const modes = [...new Set(formats.map((format) => navigationMode(template, format)))];
    if (body !== undefined) {
        for (const mode of modes) {
            if (!body.navigation.allowed.includes(mode))
                error('/layout/navigation', 'LAYOUT_NAVIGATION_NOT_ALLOWED', `La mise en page « ${layoutName} » ne propose pas la navigation « ${NAVIGATION_LABEL[mode]} ». Choisissez parmi : ${body.navigation.allowed.map((allowed) => `« ${NAVIGATION_LABEL[allowed]} »`).join(', ')}.`, { mode, allowed: body.navigation.allowed });
        }
    }
    // Pages.
    const pageIds = new Set();
    const placedOnce = new Set();
    (layout.pages ?? []).forEach((page, index) => {
        const pointer = `/layout/pages/${index}`;
        if (pageIds.has(page.page_id))
            error(`${pointer}/page_id`, 'LAYOUT_PAGE_DUPLICATE', `Deux pages portent le même nom interne (page « ${fr(page.label, 'sans nom')} »). Renommez l’une d’elles.`, { page_id: page.page_id });
        pageIds.add(page.page_id);
        for (const id of page.sections) {
            if (!sections.has(id))
                error(`${pointer}/sections`, 'LAYOUT_PAGE_SECTION_UNKNOWN', `La page « ${fr(page.label, 'sans nom')} » cite une partie qui n’existe plus dans le formulaire. Retirez-la de la page.`, { section_id: id });
            else if (placedOnce.has(id))
                error(`${pointer}/sections`, 'LAYOUT_PAGE_SECTION_DUPLICATE', `La partie « ${sectionName(id)} » est dans deux pages. Laissez-la dans une seule.`, { section_id: id });
            placedOnce.add(id);
        }
        for (const root of roots(page.visible_when)) {
            if (!PAGE_ROOTS.has(root))
                error(`${pointer}/visible_when`, 'LAYOUT_PAGE_CONDITION_ROOT', `La condition d’affichage de la page « ${fr(page.label, 'sans nom')} » ne peut porter que sur les réponses du formulaire ou les données de la tâche.`, { root });
        }
    });
    if (layout.pages !== undefined) {
        const unpaged = template.sections.filter((section) => !placedOnce.has(section.section_id));
        for (const section of unpaged)
            warn('/layout/pages', 'LAYOUT_SECTION_UNPAGED', `La partie « ${sectionName(section.section_id)} » n’est dans aucune page : elle s’affichera sur la dernière page.`, { section_id: section.section_id });
    }
    // Agrégats.
    const aggregateIds = new Set();
    (layout.aggregates ?? []).forEach((aggregate, index) => {
        const pointer = `/layout/aggregates/${index}`;
        const name = fr(aggregate.label, 'sans nom');
        if (aggregateIds.has(aggregate.aggregate_id))
            error(pointer, 'LAYOUT_AGGREGATE_DUPLICATE', `Deux totaux portent le même nom interne (« ${name} »). Renommez l’un d’eux.`, { aggregate_id: aggregate.aggregate_id });
        aggregateIds.add(aggregate.aggregate_id);
        const source = sections.get(aggregate.source);
        if (source === undefined || (source.series === undefined && source.repeat === undefined)) {
            error(`${pointer}/source`, 'LAYOUT_AGGREGATE_SOURCE_UNKNOWN', `Le total « ${name} » ne compte pas une liste de lignes du formulaire. Choisissez une liste de lignes.`, { source: aggregate.source });
            return;
        }
        for (const field of [aggregate.field, aggregate.group_by]) {
            if (field !== undefined && !source.fields.some((entry) => entry.field.field_id === field))
                error(pointer, 'LAYOUT_AGGREGATE_FIELD_UNKNOWN', `Le total « ${name} » porte sur une colonne qui n’existe pas dans « ${sectionName(aggregate.source)} ». Choisissez une colonne de la liste.`, { field });
        }
    });
    (layout.pages ?? []).forEach((page, index) => {
        if (page.counter !== undefined && !aggregateIds.has(page.counter.aggregate_ref))
            error(`/layout/pages/${index}/counter`, 'LAYOUT_COUNTER_UNKNOWN', `Le compteur de la page « ${fr(page.label, 'sans nom')} » cite un total qui n’existe pas. Choisissez un total de la liste.`, { aggregate_ref: page.counter.aggregate_ref });
    });
    (layout.warnings ?? []).forEach((warning, index) => {
        const source = warning.source === undefined ? undefined : sections.get(warning.source);
        if (warning.source !== undefined && (source === undefined || (source.series === undefined && source.repeat === undefined)))
            error(`/layout/warnings/${index}/source`, 'LAYOUT_WARNING_SOURCE_UNKNOWN', `L’avertissement « ${fr(warning.message, 'sans nom')} » ne porte pas sur une liste de lignes du formulaire. Choisissez une liste de lignes.`, { source: warning.source });
    });
    // Placements des sections.
    const sectionRegion = (id) => layout.placements?.[id]?.region ?? body?.regions.find((region) => region.accepts.includes('section'))?.region_id;
    for (const [id, placement] of Object.entries(layout.placements ?? {})) {
        const pointer = `/layout/placements/${id}`;
        if (!sections.has(id)) {
            error(pointer, 'LAYOUT_PLACEMENT_SECTION_UNKNOWN', 'Un réglage de mise en page vise une partie qui n’existe plus dans le formulaire. Supprimez ce réglage.', { section_id: id });
            continue;
        }
        if (placement.region !== undefined && body !== undefined) {
            const region = regions.get(placement.region);
            if (region === undefined)
                error(`${pointer}/region`, 'LAYOUT_REGION_UNKNOWN', `La partie « ${sectionName(id)} » est placée dans une zone qui n’existe pas dans la mise en page « ${layoutName} ». Choisissez une zone de la mise en page.`, { region: placement.region });
            else if (!region.accepts.includes('section'))
                error(`${pointer}/region`, 'LAYOUT_REGION_REFUSES_CATEGORY', `La zone « ${regionName(placement.region)} » n’accepte pas les parties du formulaire. Placez « ${sectionName(id)} » dans une autre zone.`, { region: placement.region, category: 'section' });
        }
        const section = sections.get(id);
        if (placement.entry_panel !== undefined) {
            const asTable = placement.presentation?.as === 'table' || placement.presentation?.as === 'list_detail' || (placement.presentation === undefined && section.series?.presentation === 'ledger');
            if (section.series === undefined || !asTable)
                error(`${pointer}/entry_panel`, 'LAYOUT_ENTRY_PANEL_NOT_TABLE', `Le panneau de saisie ne s’utilise qu’avec une liste de lignes présentée en tableau. Présentez « ${sectionName(id)} » en tableau, ou retirez le panneau.`);
            else if (body !== undefined && !regions.has(placement.entry_panel.region))
                error(`${pointer}/entry_panel/region`, 'LAYOUT_REGION_UNKNOWN', `Le panneau de saisie de « ${sectionName(id)} » est placé dans une zone qui n’existe pas dans la mise en page « ${layoutName} ». Choisissez une zone de la mise en page.`, { region: placement.entry_panel.region });
        }
        if (placement.presentation !== undefined && placement.presentation.as !== 'form' && section.series === undefined && section.repeat === undefined) {
            error(`${pointer}/presentation`, 'LAYOUT_PRESENTATION_NOT_LIST', `Seule une liste de lignes se présente en tableau ou en cartes. « ${sectionName(id)} » reste un formulaire.`);
        }
    }
    // Blocs.
    const blockIds = new Set();
    const perRegion = new Map();
    const recipe = mergeRecipes(context.recipes);
    const messageOf = (code, subject) => {
        for (const { body: candidate } of context.recipes) {
            const declared = candidate.messages?.[code];
            if (declared !== undefined)
                return fr(declared, GENERIC_RECIPE_MESSAGE[code](subject));
        }
        return GENERIC_RECIPE_MESSAGE[code](subject);
    };
    (layout.blocks ?? []).forEach((block, index) => {
        const pointer = `/layout/blocks/${index}`;
        const { name, range } = blockComponent(block);
        const manifest = context.components.get(name) ?? null;
        const blockName = fr(block.label, fr(manifest?.component.label, 'sans nom'));
        if (blockIds.has(block.block_id))
            error(`${pointer}/block_id`, 'LAYOUT_BLOCK_DUPLICATE', `Deux blocs portent le même nom interne (« ${blockName} »). Renommez l’un d’eux.`, { block_id: block.block_id });
        blockIds.add(block.block_id);
        if (manifest === null) {
            error(`${pointer}/component`, 'LAYOUT_BLOCK_COMPONENT_UNKNOWN', 'Un bloc utilise un composant qui n’existe pas ou n’est plus publié. Retirez ce bloc ou choisissez-en un autre dans la bibliothèque.', { component: block.component });
            return;
        }
        const declared = manifest.block;
        if (declared === undefined) {
            error(`${pointer}/component`, 'LAYOUT_BLOCK_NOT_A_BLOCK', `« ${blockName} » ne peut pas être placé dans un formulaire. Choisissez un bloc de la bibliothèque.`, { component: block.component });
            return;
        }
        if (!satisfiesRange(manifest.component.version, range))
            error(`${pointer}/component`, 'LAYOUT_BLOCK_VERSION_UNSATISFIED', `La version disponible du bloc « ${blockName} » ne correspond plus à celle prévue. Mettez le bloc à jour.`, { component: block.component, available: manifest.component.version });
        if (block.variant !== undefined && !(declared.variants ?? []).includes(block.variant))
            error(`${pointer}/variant`, 'LAYOUT_BLOCK_VARIANT_UNKNOWN', `Le bloc « ${blockName} » n’a pas cette variante. Choisissez une variante de la liste.`, { variant: block.variant });
        for (const pageId of block.page_ids ?? [])
            if (!formPages(template).some((page) => page.page_id === pageId))
                error(`${pointer}/page_ids`, 'LAYOUT_BLOCK_PAGE_UNKNOWN', `Le bloc « ${blockName} » est affiché sur une page qui n’existe pas. Choisissez ses pages dans la liste.`, { page_id: pageId });
        if (body !== undefined) {
            const region = regions.get(block.region);
            if (region === undefined)
                error(`${pointer}/region`, 'LAYOUT_REGION_UNKNOWN', `Le bloc « ${blockName} » est placé dans une zone qui n’existe pas dans la mise en page « ${layoutName} ». Choisissez une zone de la mise en page.`, { region: block.region });
            else {
                if (!region.accepts.includes(declared.category))
                    error(`${pointer}/region`, 'LAYOUT_REGION_REFUSES_CATEGORY', `La zone « ${regionName(block.region)} » n’accepte pas un bloc de ${CATEGORY_LABEL[declared.category] ?? declared.category}. Placez « ${blockName} » dans une autre zone.`, { region: block.region, category: declared.category });
                const count = (perRegion.get(block.region) ?? 0) + 1;
                perRegion.set(block.region, count);
                if (region.max_blocks !== undefined && count === region.max_blocks + 1)
                    error(`${pointer}/region`, 'LAYOUT_REGION_FULL', `La zone « ${regionName(block.region)} » contient trop de blocs (${region.max_blocks} au plus). Déplacez « ${blockName} » dans une autre zone.`, { region: block.region, max_blocks: region.max_blocks });
            }
        }
        const allowed = new Set((declared.bindings ?? []).map((binding) => binding.name));
        for (const [input, binding] of Object.entries(block.bind ?? {})) {
            if (!allowed.has(input))
                error(`${pointer}/bind/${input}`, 'LAYOUT_BINDING_UNKNOWN', `Le bloc « ${blockName} » n’a pas de donnée à relier de ce nom. Choisissez une donnée dans la liste.`, { input });
            const root = binding.path.slice(1).split('.')[0];
            if (!BINDING_ROOTS.has(root))
                error(`${pointer}/bind/${input}`, 'LAYOUT_BINDING_ROOT_FORBIDDEN', `Le bloc « ${blockName} » est relié à une donnée qui n’est pas disponible dans un formulaire. Choisissez une donnée dans la liste.`, { root });
        }
        checkDisplayBlock(name, block, pointer, blockName, { sections, pageIds: new Set(formPages(template).map((page) => page.page_id)), aggregateIds, error });
        if (recipe.forbidden_block_categories.includes(declared.category))
            error(`${pointer}/component`, 'RECIPE_BLOCK_CATEGORY_FORBIDDEN', messageOf('RECIPE_BLOCK_CATEGORY_FORBIDDEN', blockName), { category: declared.category, recipes: recipe.recipe_ids });
        if (recipe.forbidden_block_inputs.includes(declared.input))
            error(`${pointer}/component`, 'RECIPE_BLOCK_INPUT_FORBIDDEN', messageOf('RECIPE_BLOCK_INPUT_FORBIDDEN', blockName), { input: declared.input, recipes: recipe.recipe_ids });
    });
    // Une seule zone mise en avant « attention » par page (décision ambre du v1).
    for (const page of formPages(template)) {
        const attention = [
            ...page.sections.filter((id) => layout.placements?.[id]?.emphasis === 'attention'),
            ...(layout.blocks ?? []).filter((block) => block.emphasis === 'attention' && (block.page_ids === undefined || block.page_ids.includes(page.page_id))).map((block) => block.block_id),
        ];
        if (attention.length > 1)
            error('/layout', 'LAYOUT_ATTENTION_MULTIPLE', `La page « ${fr(page.label, 'principale')} » met plusieurs zones en alerte. Gardez une seule zone en alerte par page.`, { page_id: page.page_id, items: attention });
    }
    // Pied.
    for (const slot of ['start', 'center', 'end']) {
        const value = layout.footer?.[slot];
        if (value !== undefined && !FOOTER_KEYWORDS.has(value) && !blockIds.has(value))
            error(`/layout/footer/${slot}`, 'LAYOUT_FOOTER_SLOT_UNKNOWN', 'Un emplacement du pied affiche un bouton ou un bloc qui n’existe pas. Choisissez un élément de la liste.', { slot, value });
    }
    // Recette : navigation refusée en tout, ou selon la nature du formulaire.
    for (const mode of modes) {
        if (recipe.forbidden_navigation.includes(mode))
            error('/layout/navigation', 'RECIPE_NAVIGATION_FORBIDDEN', messageOf('RECIPE_NAVIGATION_FORBIDDEN', NAVIGATION_LABEL[mode]), { mode, recipes: recipe.recipe_ids });
        if (layout.intent !== undefined && (recipe.forbidden_navigation_by_intent[layout.intent] ?? []).includes(mode))
            error('/layout/navigation', 'RECIPE_NAVIGATION_FORBIDDEN_FOR_INTENT', messageOf('RECIPE_NAVIGATION_FORBIDDEN_FOR_INTENT', NAVIGATION_LABEL[mode]), { mode, intent: layout.intent, recipes: recipe.recipe_ids });
    }
    for (const category of recipe.required_block_categories) {
        const present = (layout.blocks ?? []).some((block) => context.components.get(blockComponent(block).name)?.block?.category === category);
        if (!present)
            error('/layout/blocks', 'RECIPE_BLOCK_CATEGORY_REQUIRED', messageOf('RECIPE_BLOCK_CATEGORY_REQUIRED', CATEGORY_LABEL[category] ?? category), { category, recipes: recipe.recipe_ids });
    }
    // Bandeau fixe du formulaire (invariant du 2026-10-19).
    if (body !== undefined && !body.regions.some((region) => region.role === 'header'))
        error('/layout/layout_ref', 'LAYOUT_FORM_BAR_MISSING', `La mise en page « ${layoutName} » n’a pas de bandeau en haut (date de la fiche, opérateur, « Valider », « Sortir »). Choisissez une autre mise en page.`);
    // Régions des sections dans le gabarit, même sans placement explicite.
    if (body !== undefined && !body.regions.some((region) => region.accepts.includes('section'))) {
        error('/layout/layout_ref', 'LAYOUT_NO_SECTION_REGION', `La mise en page « ${layoutName} » n’a aucune zone pour les parties du formulaire. Choisissez une autre mise en page.`);
    }
    void sectionRegion;
    return findings;
}
/**
 * Change le gabarit d'un formulaire : chaque section et chaque bloc va dans la région du nouveau
 * gabarit qui a le même rôle et admet sa catégorie ; à défaut, dans la région de dépôt, et il figure
 * au rapport (« 2 blocs sans région : Décision, Articles attendus »). Rien n'est supprimé.
 */
export function relocateLayout(template, from, to, categoryOf = () => undefined) {
    const layout = layoutOf(template) ?? {};
    const moved = [];
    const unplaced = [];
    const target = (fromRegion, category) => {
        const role = from.body.regions.find((region) => region.region_id === fromRegion)?.role;
        const match = to.body.regions.find((region) => region.role === role && region.accepts.includes(category));
        if (match !== undefined)
            return { region: match.region_id, placed: true };
        const drop = to.body.drop_region ?? to.body.regions.find((region) => region.accepts.includes(category))?.region_id ?? to.body.regions[0].region_id;
        return { region: drop, placed: false };
    };
    const sectionLabel = (id) => fr(template.sections.find((section) => section.section_id === id)?.label, 'sans nom');
    const placements = {};
    const defaultFrom = from.body.regions.find((region) => region.accepts.includes('section'))?.region_id;
    for (const section of template.sections) {
        const current = layout.placements?.[section.section_id];
        const fromRegion = current?.region ?? defaultFrom;
        const { region, placed } = target(fromRegion, 'section');
        const entry = { kind: 'section', id: section.section_id, label: sectionLabel(section.section_id), from: fromRegion ?? '', to: region };
        (placed ? moved : unplaced).push(entry);
        const next = { ...(current ?? {}), region };
        if (current?.entry_panel !== undefined) {
            const panel = target(current.entry_panel.region, 'capture');
            next.entry_panel = { ...current.entry_panel, region: panel.region };
        }
        placements[section.section_id] = next;
    }
    const blocks = (layout.blocks ?? []).map((block) => {
        const { region, placed } = target(block.region, categoryOf(block) ?? 'display');
        const entry = { kind: 'block', id: block.block_id, label: fr(block.label, block.block_id), from: block.region, to: region };
        (placed ? moved : unplaced).push(entry);
        return { ...block, region };
    });
    const allowed = to.body.navigation.allowed;
    const mode = layout.navigation?.mode !== undefined && allowed.includes(layout.navigation.mode) ? layout.navigation.mode : to.body.navigation.default;
    return {
        moved,
        unplaced,
        layout: { ...layout, layout_ref: to.artifact_id, navigation: { ...(layout.navigation ?? {}), mode }, placements, ...(blocks.length === 0 ? {} : { blocks: blocks }) },
    };
}
//# sourceMappingURL=form-layout-check.js.map